// Copyright (c) 2022. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import {
    useCallback,
    useState,
} from "react";
import { useParams } from "react-router-dom";
import { EmailTokenDTO } from "../../../../../../../io/hyperify/core/auth/email/types/EmailTokenDTO";
import { SmsTokenDTO } from "../../../../../../../io/hyperify/core/auth/sms/types/SmsTokenDTO";
import { CurrencyUtils } from "../../../../../../../io/hyperify/core/CurrencyUtils";
import { FacebookPixelUtils } from "../../../../../../../io/hyperify/core/facebook/FacebookPixelUtils";
import { ButtonStyle } from "../../../../../../../io/hyperify/core/frontend/button/ButtonStyle";
import { map } from "../../../../../../../io/hyperify/core/functions/map";
import { LogService } from "../../../../../../../io/hyperify/core/LogService";
import { PaytrailCreatePaymentDTO } from "../../../../../../../io/hyperify/core/paytrail/dtos/PaytrailCreatePaymentDTO";
import { PaytrailPaymentMethodGroup } from "../../../../../../../io/hyperify/core/paytrail/types/PaytrailPaymentMethodGroup";
import { PaytrailPaymentMethodGroupData } from "../../../../../../../io/hyperify/core/paytrail/types/PaytrailPaymentMethodGroupData";
import { PaytrailProvider } from "../../../../../../../io/hyperify/core/paytrail/types/PaytrailProvider";
import { InvoiceDTO } from "../../../../../../../io/hyperify/core/store/types/invoice/InvoiceDTO";
import { InvoiceRowDTO } from "../../../../../../../io/hyperify/core/store/types/invoice/InvoiceRowDTO";
import { Currency } from "../../../../../../../io/hyperify/core/types/Currency";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";
import { TranslationParams } from "../../../../../../../io/hyperify/core/types/TranslationParams";
import { Button } from "../../../../../../../io/hyperify/frontend/components/button/Button";
import { Card } from "../../../../../../../io/hyperify/frontend/components/card/Card";
import { DateCard } from "../../../../../../../io/hyperify/frontend/components/card/date/DateCard";
import { CardGrid } from "../../../../../../../io/hyperify/frontend/components/cardGrid/CardGrid";
import { Loader } from "../../../../../../../io/hyperify/frontend/components/loader/Loader";
import { SubmitButton } from "../../../../../../../io/hyperify/frontend/components/submitButton/SubmitButton";
import { TextColumn } from "../../../../../../../io/hyperify/frontend/components/table/columns/text/TextColumn";
import { TimeColumn } from "../../../../../../../io/hyperify/frontend/components/table/columns/time/TimeColumn";
import { Table } from "../../../../../../../io/hyperify/frontend/components/table/Table";
import { TableBody } from "../../../../../../../io/hyperify/frontend/components/table/TableBody";
import { TableColumn } from "../../../../../../../io/hyperify/frontend/components/table/TableColumn";
import { TableHeader } from "../../../../../../../io/hyperify/frontend/components/table/TableHeader";
import { TableHeaderColumn } from "../../../../../../../io/hyperify/frontend/components/table/TableHeaderColumn";
import { TableRow } from "../../../../../../../io/hyperify/frontend/components/table/TableRow";
import { AuthSessionService } from "../../../../../../../io/hyperify/frontend/services/AuthSessionService";
import { RouteService } from "../../../../../../../io/hyperify/frontend/services/RouteService";
import { FACEBOOK_PIXEL_ID } from "../../../../../core/constants/ads";
import {
    INVOICE_BANK_ACCOUNT_NAME,
    INVOICE_BANK_ACCOUNT_BIC,
    INVOICE_BANK_ACCOUNT_IBAN,
} from "../../../../../core/constants/bankAccount";
import { MY_INVOICE_VIEW_CLASS_NAME } from "../../../../../core/constants/className";
import { PAYTRAIL_ENABLED } from "../../../../../core/constants/company";
import { GET_MY_CLIENT_INVENTORY_ITEM_ROUTE } from "../../../../../core/constants/route";
import {
    T_DATE_FORMAT,
    T_MY_INVOICE_LIST_VIEW_TABLE_STATUS_CANCELLED,
    T_MY_INVOICE_LIST_VIEW_TABLE_STATUS_PAID,
    T_MY_INVOICE_LIST_VIEW_TABLE_STATUS_UNPAID,
    T_MY_INVOICE_VIEW_BANK_NAME_LABEL,
    T_MY_INVOICE_VIEW_BIC_LABEL,
    T_MY_INVOICE_VIEW_DATE_LABEL,
    T_MY_INVOICE_VIEW_DUE_DATE_LABEL,
    T_MY_INVOICE_VIEW_IBAN_LABEL,
    T_MY_INVOICE_VIEW_INVOICE_ID_LABEL,
    T_MY_INVOICE_VIEW_NO_RESULTS_FOUND,
    T_MY_INVOICE_VIEW_PAYMENT_FOOTER_DETAILS,
    T_MY_INVOICE_VIEW_PAYMENT_GROUP_BACK_BUTTON,
    T_MY_INVOICE_VIEW_PAYMENT_GROUP_SUBMIT_BUTTON,
    T_MY_INVOICE_VIEW_PAYMENT_IBAN_DETAILS_TITLE,
    T_MY_INVOICE_VIEW_PAYMENT_IBAN_TITLE,
    T_MY_INVOICE_VIEW_PAYMENT_PAYTRAIL_TITLE,
    T_MY_INVOICE_VIEW_PAYMENT_PROVIDER_SUBMIT_BUTTON,
    T_MY_INVOICE_VIEW_PAYMENT_PROVIDER_TITLE,
    T_MY_INVOICE_VIEW_REFERENCE_NUMBER_LABEL,
    T_MY_INVOICE_VIEW_ROW_LABEL_AMOUNT_DATE,
    T_MY_INVOICE_VIEW_ROW_LABEL_DESCRIPTION,
    T_MY_INVOICE_VIEW_ROW_LABEL_END_DATE,
    T_MY_INVOICE_VIEW_ROW_LABEL_INVENTORY_BUTTON,
    T_MY_INVOICE_VIEW_ROW_LABEL_PRICE_DATE,
    T_MY_INVOICE_VIEW_ROW_LABEL_START_DATE,
    T_MY_INVOICE_VIEW_ROW_LABEL_VAT_PERCENT_DATE,
    T_MY_INVOICE_VIEW_STATE_LABEL,
    T_MY_INVOICE_VIEW_TITLE,
    T_MY_INVOICE_VIEW_TOTAL_OPEN_LABEL,
    T_MY_INVOICE_VIEW_TOTAL_PAID_LABEL,
    T_MY_INVOICE_VIEW_TOTAL_SUM_INCLUDING_VAT_LABEL,
    T_MY_INVOICE_VIEW_TOTAL_SUM_LABEL,
    T_MY_INVOICE_VIEW_TOTAL_VAT_LABEL,
} from "../../../../../core/constants/translation";
import { SendanorClientHttpService } from "../../../../../core/services/SendanorClientHttpService";
import { useMyInvoice } from "../../../../hooks/useMyInvoice";
import "./MyInvoiceView.scss";
import { FacebookPixel } from "../../../common/facebookPixel/FacebookPixel";

const LOG = LogService.createLogger( 'MyInvoiceView' );

export interface MyInvoiceViewProps {
    readonly t : TranslationFunction;
    readonly className? : string;
}

export function MyInvoiceView ( props : MyInvoiceViewProps ) {
    const t = props?.t;
    const className = props?.className;
    const params = useParams();
    const clientId = params?.clientId;
    const invoiceId = params?.invoiceId;
    const [ invoice ] = useMyInvoice( invoiceId, clientId );
    const rowList : readonly InvoiceRowDTO[] = invoice?.rows ?? [];
    const notFound = invoice === null;
    const isLoading = invoice === undefined;
    const translationParams : TranslationParams = {
        INVOICE_ID : invoiceId ?? '',
    };
    const dateFormat = t( T_DATE_FORMAT );

    const [ newTransaction, setNewTransaction ] = useState<PaytrailCreatePaymentDTO | undefined>( undefined );

    const goInventoryItem = useCallback(
        ( inventoryItemId : string ) => {
            const route = GET_MY_CLIENT_INVENTORY_ITEM_ROUTE( clientId ?? '', inventoryItemId );
            LOG.debug( `Going to inventory route: `, route );
            RouteService.setRoute( route );
        }, [
            clientId,
        ],
    );

    const clearPaymentGroup = useCallback(
        () : void => {
            // FIXME: Cancel the Paytrail payment transaction
            setNewTransaction( undefined );
        },
        [],
    );

    const selectPaymentGroup = useCallback(
        ( group : PaytrailPaymentMethodGroup ) : void => {

            LOG.debug( `selectPaymentGroup: group: `, group );

            const token : EmailTokenDTO | SmsTokenDTO | undefined = AuthSessionService.getToken();
            if ( !token || !clientId || !invoiceId ) {
                LOG.debug( `token = `, token );
                LOG.debug( `clientId = `, clientId );
                LOG.debug( `invoiceId = `, invoiceId );
                LOG.warn( 'selectPaymentGroup: You must have a valid session, clientId and invoiceId' );
            } else {
                SendanorClientHttpService.createPaymentTransaction( group, clientId, invoiceId, token ).then( ( dto : InvoiceDTO ) => {
                    setNewTransaction( dto?.newTransaction );
                    LOG.debug( `new transaction: `, dto?.newTransaction );
                } ).catch( ( err ) => {
                    LOG.error( `There was an error: `, err );
                    setNewTransaction( undefined );
                } );
            }

        }, [
            clientId,
            invoiceId,
        ],
    );

    const isTerminated = !!invoice?.isTerminated;
    const isPaid = !!invoice?.isPaid;

    const iban = INVOICE_BANK_ACCOUNT_IBAN;
    const bic = INVOICE_BANK_ACCOUNT_BIC;
    const bankName = INVOICE_BANK_ACCOUNT_NAME;
    const referenceNumber = invoice?.referenceNumber ?? '';

    return (
        <div
            className={
                MY_INVOICE_VIEW_CLASS_NAME
                + (className ? ` ${ className }` : '')
            }
        >

            <h3>{ t( T_MY_INVOICE_VIEW_TITLE, translationParams ) }</h3>

            <div className={ `${ MY_INVOICE_VIEW_CLASS_NAME }-details` }>

                { isLoading ? <Loader /> : null }

                { notFound ? (
                    <p>{ t( T_MY_INVOICE_VIEW_NO_RESULTS_FOUND, translationParams ) }</p>
                ) : null }

                { !isLoading && !notFound ?
                    <>

                        <CardGrid>
                            <Card label={ t( T_MY_INVOICE_VIEW_INVOICE_ID_LABEL ) }>{ invoice?.invoiceId }</Card>
                            <Card label={ t( T_MY_INVOICE_VIEW_STATE_LABEL ) }>{
                                invoice?.isPaid
                                    ? t(T_MY_INVOICE_LIST_VIEW_TABLE_STATUS_PAID)
                                    : (
                                        invoice?.isTerminated
                                            ? t(T_MY_INVOICE_LIST_VIEW_TABLE_STATUS_CANCELLED)
                                            : t(T_MY_INVOICE_LIST_VIEW_TABLE_STATUS_UNPAID)
                                    )
                            }</Card>
                            <DateCard
                                label={ t( T_MY_INVOICE_VIEW_DATE_LABEL ) }
                                value={ invoice?.date }
                                format={ dateFormat }
                            />
                            <DateCard
                                label={ t( T_MY_INVOICE_VIEW_DUE_DATE_LABEL ) }
                                value={ invoice?.dueDate }
                                format={ dateFormat }
                            />
                            <Card label={ t( T_MY_INVOICE_VIEW_REFERENCE_NUMBER_LABEL ) }>{ invoice?.referenceNumber }</Card>
                        </CardGrid>

                        <Card>
                            <Table>
                                <TableHeader>
                                    <TableHeaderColumn>{ t( T_MY_INVOICE_VIEW_ROW_LABEL_START_DATE ) }</TableHeaderColumn>
                                    <TableHeaderColumn>{ t( T_MY_INVOICE_VIEW_ROW_LABEL_END_DATE ) }</TableHeaderColumn>
                                    <TableHeaderColumn>{ t( T_MY_INVOICE_VIEW_ROW_LABEL_DESCRIPTION ) }</TableHeaderColumn>
                                    <TableHeaderColumn>{ t( T_MY_INVOICE_VIEW_ROW_LABEL_AMOUNT_DATE ) }</TableHeaderColumn>
                                    <TableHeaderColumn>{ t( T_MY_INVOICE_VIEW_ROW_LABEL_PRICE_DATE ) }</TableHeaderColumn>
                                    <TableHeaderColumn>{ t( T_MY_INVOICE_VIEW_ROW_LABEL_VAT_PERCENT_DATE ) }</TableHeaderColumn>
                                    { isPaid ? (
                                        <TableHeaderColumn></TableHeaderColumn>
                                    ) : null }
                                </TableHeader>
                                <TableBody>{ map(
                                    rowList,
                                    ( row ) => {
                                        return (
                                            <TableRow key={ `invoice-row-${ row?.invoiceRowId }` }>
                                                <TimeColumn
                                                    value={ row?.startDate }
                                                    format={ dateFormat }
                                                />
                                                <TimeColumn
                                                    value={ row?.endDate }
                                                    format={ dateFormat }
                                                />
                                                <TextColumn value={ row?.description } />
                                                <TextColumn value={ `${ row?.amount }x` } />
                                                <TextColumn value={ `${ CurrencyUtils.getSumWithVat( row?.price, 1, row?.vatPercent, row?.discountPercent )?.toFixed( 2 ) }` } />
                                                <TextColumn value={ `${ (row?.vatPercent * 100).toFixed( 1 ) }%` } />
                                                { isPaid ? (
                                                    <TableColumn>
                                                        { row.inventoryItemId && row.inventoryItemId !== '0' ? (
                                                            <Button
                                                                click={ () => goInventoryItem( row.inventoryItemId ) }
                                                                style={ ButtonStyle.SECONDARY }
                                                            >{
                                                                t( T_MY_INVOICE_VIEW_ROW_LABEL_INVENTORY_BUTTON )
                                                            }</Button>
                                                        ) : null }
                                                    </TableColumn>
                                                ) : null }
                                            </TableRow>
                                        );
                                    },
                                ) }
                                </TableBody>
                            </Table>
                        </Card>

                        <CardGrid>
                            <Card label={ t( T_MY_INVOICE_VIEW_TOTAL_SUM_LABEL ) }>{ invoice?.totalSum?.toFixed( 2 ) } €</Card>
                            <Card label={ t( T_MY_INVOICE_VIEW_TOTAL_VAT_LABEL ) }>{ invoice?.totalVat?.toFixed( 2 ) } €</Card>
                            <Card label={ t( T_MY_INVOICE_VIEW_TOTAL_SUM_INCLUDING_VAT_LABEL ) }>{ invoice?.totalSumIncludingVat?.toFixed( 2 ) } €</Card>
                            <Card label={ t( T_MY_INVOICE_VIEW_TOTAL_PAID_LABEL ) }>{ invoice?.totalPaid?.toFixed( 2 ) } €</Card>
                        </CardGrid>

                        { !isPaid && !isTerminated ? (
                            <>
                                { invoice?.payment && PAYTRAIL_ENABLED ? (
                                    <>
                                        <h3>{ t( T_MY_INVOICE_VIEW_PAYMENT_PAYTRAIL_TITLE, translationParams ) }</h3>

                                        { newTransaction === undefined ? (
                                                <>
                                                    { invoice?.payment?.terms ? (
                                                        <p
                                                            className={ MY_INVOICE_VIEW_CLASS_NAME + '-payment-terms' }
                                                            dangerouslySetInnerHTML={ { __html : invoice?.payment?.terms } }
                                                        />
                                                    ) : null }

                                                    <CardGrid className={ MY_INVOICE_VIEW_CLASS_NAME + '-payment-options' }>
                                                        { map(
                                                            invoice?.payment?.groups,
                                                            ( group : PaytrailPaymentMethodGroupData ) => {
                                                                return (
                                                                    <Card
                                                                        key={ 'paytrail-group-' + group?.id }
                                                                        label={ group?.name }
                                                                    >{ map(
                                                                        group?.providers,
                                                                        ( provider ) => {
                                                                            return (
                                                                                <img
                                                                                    className={ MY_INVOICE_VIEW_CLASS_NAME + '-payment-provider' }
                                                                                    key={ 'paytrail-group-' + group?.id + provider?.id }
                                                                                    src={ provider?.icon }
                                                                                    alt={ provider.name }
                                                                                />
                                                                            );
                                                                        },
                                                                    ) }

                                                                        <Button
                                                                            click={ () => selectPaymentGroup( group?.id ) }
                                                                            style={ ButtonStyle.PRIMARY }
                                                                            className={ MY_INVOICE_VIEW_CLASS_NAME + '-select-payment-provider' }
                                                                        >{ t( T_MY_INVOICE_VIEW_PAYMENT_GROUP_SUBMIT_BUTTON, translationParams ) }</Button>

                                                                    </Card>
                                                                );
                                                            },
                                                        ) }
                                                    </CardGrid>

                                                </>)
                                            : (<>

                                                    <h3>{ t( T_MY_INVOICE_VIEW_PAYMENT_PROVIDER_TITLE, translationParams ) }</h3>

                                                    <CardGrid className={ MY_INVOICE_VIEW_CLASS_NAME + '-provider-options' }>
                                                        { map(
                                                            newTransaction?.providers,
                                                            ( provider : PaytrailProvider ) => {
                                                                return <Card
                                                                    key={ 'paytrail-provider-card-' + provider?.id }
                                                                    className={ MY_INVOICE_VIEW_CLASS_NAME + '-provider-card' }
                                                                >
                                                                    <form
                                                                        target="_self"
                                                                        method="POST"
                                                                        action={ provider.url }
                                                                    >

                                                                        { map(
                                                                            provider.parameters,
                                                                            ( parameter ) => (
                                                                                <input
                                                                                    type="hidden"
                                                                                    name={ parameter.name }
                                                                                    value={ parameter.value }
                                                                                />
                                                                            ),
                                                                        ) }
                                                                        <img
                                                                            className={ MY_INVOICE_VIEW_CLASS_NAME + '-payment-provider' }
                                                                            key={ 'paytrail-provider-img-' + provider?.id }
                                                                            src={ provider?.icon }
                                                                            alt={ provider.name }
                                                                        />
                                                                        <SubmitButton
                                                                            key={ 'paytrail-provider-button-' + provider?.id }
                                                                            style={ ButtonStyle.PRIMARY }
                                                                            className={ MY_INVOICE_VIEW_CLASS_NAME + '-select-payment-provider' }
                                                                        >{ t( T_MY_INVOICE_VIEW_PAYMENT_PROVIDER_SUBMIT_BUTTON, { ...translationParams, PROVIDER_NAME : provider?.name } ) }</SubmitButton>

                                                                    </form>
                                                                </Card>;
                                                            },
                                                        ) }
                                                    </CardGrid>

                                                    <Button
                                                        click={ () => clearPaymentGroup() }
                                                        style={ ButtonStyle.SECONDARY }
                                                        className={ MY_INVOICE_VIEW_CLASS_NAME + '-select-payment-provider' }
                                                    >{ t( T_MY_INVOICE_VIEW_PAYMENT_GROUP_BACK_BUTTON, translationParams ) }</Button>

                                                </>
                                            ) }

                                    </>
                                ) : null }


                                <h3>{ t( T_MY_INVOICE_VIEW_PAYMENT_IBAN_TITLE, translationParams ) }</h3>

                                <p>{ t( T_MY_INVOICE_VIEW_PAYMENT_IBAN_DETAILS_TITLE, translationParams ) }</p>

                                <CardGrid className={ `${ MY_INVOICE_VIEW_CLASS_NAME }-bank-details` }>
                                    <Card label={ t( T_MY_INVOICE_VIEW_BANK_NAME_LABEL ) }>{ bankName }</Card>
                                    <Card label={ t( T_MY_INVOICE_VIEW_BIC_LABEL ) }>{ bic }</Card>
                                </CardGrid>

                                <CardGrid>
                                    <Card label={ t( T_MY_INVOICE_VIEW_IBAN_LABEL ) }>{ iban }</Card>
                                    <Card label={ t( T_MY_INVOICE_VIEW_REFERENCE_NUMBER_LABEL ) }>{ referenceNumber }</Card>
                                    <Card label={ t( T_MY_INVOICE_VIEW_TOTAL_OPEN_LABEL ) }>{ invoice?.totalOpen?.toFixed( 2 ) } €</Card>
                                </CardGrid>

                            </>
                        ) : null }

                        <p>{ t( T_MY_INVOICE_VIEW_PAYMENT_FOOTER_DETAILS, translationParams ) }</p>

                    </>
                    : null }
            </div>

            {isPaid? (
                <FacebookPixel url={FacebookPixelUtils.getPurchaseURL(FACEBOOK_PIXEL_ID, invoice?.totalSum ?? 0, Currency.EUR )} />
                ) : (
                <FacebookPixel url={FacebookPixelUtils.getInitiateCheckoutURL(FACEBOOK_PIXEL_ID, invoice?.totalSum ?? 0, Currency.EUR )} />
            )}

        </div>
    );
}
